import { Injectable } from '@angular/core';
import { ApiService } from '../http/api.service';
import { BehaviorSubject, Observable, tap, map } from 'rxjs';
import {
  Organization,
  OrganizationInvitation,
  InvitationStatus,
  OrganizationUser,
  OrganizationBilling,
  OrganizationBillingDetail,
  OrganizationStatus,
  OrganizationResponse
} from '../../models/organization.model';
import {
  Role,
  RolePermission,
  Feature,
  FeatureAction
} from '../../models/role.model';
import {
  User,
  OrganizationRole,
  OrganizationRoleStatus
} from '../../models/user.model';
import { AuthService } from '../auth/services/auth.service';

export interface CreateOrganizationDto {
  name: string;
  email: string;
  phone: string;
  address: {
    street: string;
    city: string;
    state: string;
    country: string;
    zipCode: string;
  };
}

export interface InviteUserDto {
  email: string;
  roleId: string;
}

@Injectable({
  providedIn: 'root'
})
export class OrganizationService {

  private organizationsSubject = new BehaviorSubject<Organization[]>([]);
  organizations$ = this.organizationsSubject.asObservable();
  private initialized = false;

  private currentOrganizationSubject = new BehaviorSubject<Organization | undefined>(undefined);
  currentOrganization$ = this.currentOrganizationSubject.asObservable();

  private readonly BASE_PATH = 'admin/organizations';

  constructor(
    private apiService: ApiService,
    private authService: AuthService
  ) {
    // Subscribe to token validation status
    this.authService.isTokenValidated().subscribe(isValidated => {
      if (isValidated && !this.initialized) {
        this.initialize();
      }
    });
  }

  public initialize(): void {
    if(!this.initialized){
      this.initialized = true;
      // this.getUserOrganizations().subscribe();
    }
  }

  getCurrentOrganization(): Organization | undefined {
    return this.currentOrganizationSubject.getValue();
  }

  setCurrentOrganization(organization: Organization): void {
    // console.log('setCurrentOrganization', organization);
    this.currentOrganizationSubject.next(organization);
    this.apiService.get(`users/${organization.id}/set-current`).subscribe(settings => {
      console.log('settings', settings);
    });
  }

  // Organization Management
  getOrganizations(params: { page?: number; limit?: number; search?: string; status?: OrganizationStatus; sort?: string; order?: string; } = {}): Observable<OrganizationResponse> {
    return this.apiService.get<OrganizationResponse>(this.BASE_PATH, params);
  }

  getOrganization(id: string): Observable<Organization> {
    return this.apiService.get<{ data: Organization; success: boolean }>(`${this.BASE_PATH}/${id}`).pipe(
      map(response => response.data)
    );
  }

  createOrganization(data: Partial<Organization>): Observable<Organization> {
    return this.apiService.post<{ data: Organization; success: boolean }>(this.BASE_PATH, data).pipe(
      map(response => response.data)
    );
  }

  updateOrganization(id: string, data: Partial<Organization>): Observable<Organization> {
    return this.apiService.put<Organization>(`${this.BASE_PATH}/${id}`, data);
  }

  deleteOrganization(id: string): Observable<{ success: boolean; message: string }> {
    return this.apiService.delete<{ success: boolean; message: string }>(`${this.BASE_PATH}/${id}`);
  }

  // Organization Users
  getOrganizationUsers(organizationId: string): Observable<OrganizationUser[]> {
    return this.apiService.get<OrganizationUser[]>(`${this.BASE_PATH}/${organizationId}/users`);
  }

  addOrganizationUser(organizationId: string, data: Partial<OrganizationUser>): Observable<OrganizationUser> {
    return this.apiService.post<OrganizationUser>(`${this.BASE_PATH}/${organizationId}/users`, data);
  }

  updateOrganizationUser(organizationId: string, userId: string, data: Partial<OrganizationUser>): Observable<OrganizationUser> {
    return this.apiService.put<OrganizationUser>(`${this.BASE_PATH}/${organizationId}/users/${userId}`, data);
  }

  removeOrganizationUser(organizationId: string, userId: string): Observable<void> {
    return this.apiService.delete<void>(`${this.BASE_PATH}/${organizationId}/users/${userId}`);
  }

  // Organization Billing
  getOrganizationBillings(organizationId: string): Observable<OrganizationBilling[]> {
    return this.apiService.get<OrganizationBilling[]>(`${this.BASE_PATH}/${organizationId}/billings`);
  }

  getOrganizationBilling(organizationId: string, billingId: string): Observable<OrganizationBilling> {
    return this.apiService.get<OrganizationBilling>(`${this.BASE_PATH}/${organizationId}/billings/${billingId}`);
  }

  getBillingDetails(organizationId: string, billingId: string): Observable<OrganizationBillingDetail[]> {
    return this.apiService.get<OrganizationBillingDetail[]>(`${this.BASE_PATH}/${organizationId}/billings/${billingId}/details`);
  }

  // Organization Codes
  getOrganizationCodes(organizationId: string): Observable<any[]> {
    return this.apiService.get<any[]>(`${this.BASE_PATH}/${organizationId}/codes`);
  }

  createOrganizationCode(organizationId: string, data: any): Observable<any> {
    return this.apiService.post<any>(`${this.BASE_PATH}/${organizationId}/codes`, data);
  }

  updateOrganizationCode(organizationId: string, codeId: string, data: any): Observable<any> {
    return this.apiService.put<any>(`${this.BASE_PATH}/${organizationId}/codes/${codeId}`, data);
  }

  deleteOrganizationCode(organizationId: string, codeId: string): Observable<void> {
    return this.apiService.delete<void>(`${this.BASE_PATH}/${organizationId}/codes/${codeId}`);
  }

  // Team Management
  getOrganizationMembers(): Observable<{ members: User[], invites: string[] }> {
    const orgId: string = this.getCurrentOrganization()?.id!;
    return this.apiService.get<{ members: User[], invites: string[] }>(`organizations/${orgId}/members`);
  }

  inviteUser(data: InviteUserDto): Observable<OrganizationInvitation> {
    const orgId: string = this.getCurrentOrganization()?.id!;
    return this.apiService.post<OrganizationInvitation>(`organizations/${orgId}/members/invite`, [data]);
  }

  inviteUsers(data: InviteUserDto[], orgId?: string): Observable<OrganizationInvitation[]> {
    orgId = orgId || this.getCurrentOrganization()?.id!;
    return this.apiService.post<OrganizationInvitation[]>(`organizations/${orgId}/members/invite`, data);
  }

  cancelInvitation(inviteId: string): Observable<void> {
    const orgId: string = this.getCurrentOrganization()?.id!;
    return this.apiService.delete<void>(`organizations/${orgId}/members/invites/${inviteId}`);
  }

  removeMember(userId: string): Observable<void> {
    const orgId: string = this.getCurrentOrganization()?.id!;
    return this.apiService.delete<void>(`organizations/${orgId}/members/${userId}`);
  }

  updateMemberRole(userId: string, roleId: string): Observable<User> {
    const orgId: string = this.getCurrentOrganization()?.id!;
    return this.apiService.put<User>(`organizations/${orgId}/members/${userId}`, { roleId });
  }

  // Role Management
  getOrganizationRoles(): Observable<Role[]> {
    const orgId: string = this.getCurrentOrganization()?.id!;
    return this.apiService.get<Role[]>(`organizations/${orgId}/roles`);
  }

  createRole(data: Partial<Role>): Observable<Role> {
    const orgId: string = this.getCurrentOrganization()?.id!;
    return this.apiService.post<Role>(`organizations/${orgId}/roles`, data);
  }

  updateRole(roleId: string, data: Partial<Role>): Observable<Role> {
    const orgId: string = this.getCurrentOrganization()?.id!;
    return this.apiService.put<Role>(`organizations/${orgId}/roles/${roleId}`, data);
  }

  deleteRole(roleId: string): Observable<void> {
    const orgId: string = this.getCurrentOrganization()?.id!;
    return this.apiService.delete<void>(`organizations/${orgId}/roles/${roleId}`);
  }

  // Permission Management
  getFeatures(): Observable<Feature[]> {
    return this.apiService.get<Feature[]>('features');
  }

  updateRolePermissions(roleId: string, permissions: RolePermission[]): Observable<Role> {
    const orgId: string = this.getCurrentOrganization()?.id!;
    return this.apiService.put<Role>(
      `organizations/${orgId}/roles/${roleId}/permissions`,
      { permissions }
    );
  }

  // Helper method to check if user has specific permission
  hasPermission(
    organizationRole: OrganizationRole,
    featureId: number,
    requiredActions: number[]
  ): boolean {
    // if (!organizationRole || !organizationRole.role) {
    //   return false;
    // }

    // const permission = organizationRole.role.permissions.find(
    //   p => p.featureId === featureId
    // );

    // if (!permission) {
    //   return false;
    // }
    return true;
    // return requiredActions.every(action =>
    //   permission.actions.includes(action)
    // );
  }

  reset(): void {
    this.organizationsSubject.next([]);
    this.currentOrganizationSubject.next(undefined);
    this.initialized = false;
  }
} 